/** @jsxImportSource @emotion/react */

import { useState, useEffect, Fragment } from 'react';
import { css } from '@emotion/react';
import { connect } from 'unistore/react';
import { Else, If, LoaderSmallPulse, Then } from '@buyhatke/react-ui';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import BbTooltips from '@components/Tooltips';
import { Link, Button } from '@components';

import {
	actionsCombiner,
	monitorActions,
	sidebarActions,
	loginActions,
} from '@utils/store/actions';
import { getWSLSKey, requestIdleCb } from '@utils/customLib';
import { getAppLinkFromCountry, getCountryProps } from '@utils/params';

import bell_icon from '@img/bell.svg';
import user_icon from '@img/user.svg';
import menu_icon from '@img/v2/grid-menu-icon.svg';
import bitdroplet_icon from '@img/v2/bitdroplet.svg';
import bitbns_academy_icon from '@img/v2/bitbns-academy.svg';
import app_store_icon from '@img/v2/app-store-icon.svg';
import chrome_icon from '@img/v2/chrome-icon.svg';
import play_store_icon from '@img/v2/play-store-icon.svg';
import ascent_icon from '@img/v2/ascent_icon.svg';
import poolSalesIcon from '@img/v2/poolSales_icon.svg';
import launchpadIcon from '@img/v2/launchpad_icon.svg';
import hardWalletIcon from '@img/v2/hardWallet_icon.svg';

const RenderNavLinks = ({ rightAligned = false, countryProps, country }) => {
	const pulseFutures = getWSLSKey('hasSeenFutures') !== true;
	const { t } = useTranslation(['common'], {
		useSuspense: false,
	});
	const countryParam = country ? `?country=${country}` : '';

	return (
		<Fragment>
			<div className="items-center flex sm:hidden">
				<BbTooltips
					hoverFocus
					noTriangle
					noTransform
					className="c-hdr__items--right flex hover:bg-primary500 rounded-md"
					position="bottomLeft"
					wrappedContent={
						<div className="c-links--light c-btn--iconTri tri-bottom c-btn--vtiny p-4 mx-2 inline-block cursor-pointer text-white whitespace-nowrap">
							<span className="text-sm">{t('Orders')}</span>
						</div>
					}
				>
					<div className="text-primary capitalize text-left max-w-xs">
						<a
							className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
							href={`/trade/${countryParam}#/activity/`}
						>
							{t('common:openOrders')}
						</a>
						<a
							className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
							href={`/trade/${countryParam}#/activity/trade-history`}
						>
							{t('common:tradeHistory')}
						</a>
						<a
							className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
							href={`/trade/${countryParam}#/activity/activity/trade-refund-history`}
						>
							{t('common:tradeRefundHistory')}
						</a>
						<If condition={countryProps.noMargin}>
							<Else>
								<p className="bg-gray-200 text-gray-700 bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 py-2 text-sm">
									{t('common:Margin')}
								</p>
								<a
									className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
									href={`/trade/${countryParam}#/activity/margin-openorder`}
								>
									{t('common:marginOpenOrder')}
								</a>
								<a
									className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
									href={`/trade/${countryParam}#/activity/margin-ongoing-trades`}
								>
									{t('common:ongoingTrades')}
								</a>
								<a
									className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
									href={`/trade/${countryParam}#/activity/margin-history`}
								>
									{t('common:History')}
								</a>
							</Else>
						</If>
						<If condition={countryProps.noFd}>
							<Else>
								<p className="bg-gray-200 text-gray-700 bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 py-2 text-sm">
									{t('Fixed Income', {
										ns: 'common',
									})}
								</p>
								<a
									className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
									href={`/trade/${countryParam}#/activity/fixed-deposits-ongoing`}
								>
									{t('common:Ongoing')}
								</a>
								<a
									className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
									href={`/trade/${countryParam}#/activity/fixed-deposits-completed`}
								>
									{t('common:Completed')}
								</a>
							</Else>
						</If>
					</div>
				</BbTooltips>
				<a
					href={`/trade/${countryParam}#/wallets/`}
					className="c-hdr__items--right text-sm p-4 mx-2 rounded-md hover:bg-primary500"
					title="Wallets"
				>
					{t('common:Wallets')}
				</a>
			</div>
			<a
				href={`/trade/${countryParam}#/markets`}
				className="hover:bg-primary500 text-sm p-4 mx-2 rounded-md tracking-wider"
				title="Markets"
			>
				{t('common:Markets')}
			</a>
			<If condition={countryProps.noMargin}>
				<Else>
					<a
						href={`/trade/${countryParam}#/margin-trades/`}
						className="hover:bg-primary500 text-sm p-4 mx-2 rounded-md tracking-wider"
						title="Margin Trades"
					>
						{t('common:Margin')}
					</a>
				</Else>
			</If>
			<div className="flex c-hdr__items--right">
				<BbTooltips
					hoverFocus
					arrow={false}
					noTransform
					className="c-hdr__items--right flex hover:bg-primary500 rounded-md"
					position={rightAligned ? 'bottomLeft' : 'bottomRight'}
					wrappedContent={
						<div className="c-links--light c-btn--iconTri tri-bottom c-btn--vtiny p-4 mx-2 inline-block cursor-pointer text-white">
							<a
								href={`/trade/${countryParam}#/btc`}
								className="text-sm hidden md:inline-block"
								title="Trading"
							>
								{t('common:Trade')}
							</a>
							<span className="text-sm md:hidden">
								{t('common:Trade')}
							</span>
						</div>
					}
				>
					<div className="text-primary capitalize text-left">
						<a
							href={`/trade/${countryParam}#/btc`}
							className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine"
							title="Trading"
						>
							{t('Start Trading', {
								ns: 'common',
							})}
						</a>
						{countryProps.hasP2P ? (
							<a
								href={`/trade/${countryParam}#/wallets/deposit/p2p/usdt/`}
								className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine"
								title="Quick Swap"
							>
								{t('usdtp2P', {
									ns: 'common',
								})}
							</a>
						) : (
							''
						)}
						{countryProps.noQuickSwap ? (
							<a
								href={`/trade/${countryParam}#/quick-swap/`}
								className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine"
								title="Quick Swap"
							>
								{t('quickSwap', {
									ns: 'common',
								})}
							</a>
						) : (
							''
						)}
					</div>
				</BbTooltips>
			</div>
			<If condition={countryProps.noFutures || countryProps.noOptions}>
				<Else>
					<BbTooltips
						hoverFocus
						noTransform
						className="c-hdr__items--right flex hover:bg-primary500 rounded-md"
						arrow={false}
						position={rightAligned ? 'bottomLeft' : 'bottomRight'}
						wrappedContent={
							<div className="c-links--light c-btn--iconTri tri-bottom c-btn--vtiny p-4 mx-2 inline-block cursor-pointer text-white">
								<If condition={pulseFutures}>
									<Then>
										<LoaderSmallPulse
											style={{
												left: '5px',
												top: '5px',
											}}
											className="text-redLighter absolute"
										/>
									</Then>
								</If>
								<span className="text-sm">
									{t('common:Derivatives')}
								</span>
							</div>
						}
					>
						<div className="text-primary capitalize text-left">
							<If condition={countryProps.noFutures}>
								<Else>
									<a
										className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine"
										href={`/trade/${countryParam}#/futures/`}
									>
										<If condition={pulseFutures}>
											<Then>
												<LoaderSmallPulse
													style={{
														left: '5px',
														top: '5px',
													}}
													className="text-redLighter absolute"
												/>
											</Then>
										</If>
										{t('common:futures')}
									</a>
								</Else>
							</If>
							<If condition={countryProps.noOptions}>
								<Else>
									<a
										className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine"
										href={`/trade/${countryParam}#/options-testnet/`}
									>
										{t('common:optionsTestnet')}
									</a>
									{/* <Link
						className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine"
						to="/futures-testnet/"
					>
						Futures (Testnet)
					</Link> */}
									<p className="text-gray-700 py-4 px-5 bg-gray-200 text-sm">
										{t('common:optionsMainnetCS')}
									</p>
								</Else>
							</If>
						</div>
					</BbTooltips>
				</Else>
			</If>
			<If condition={countryProps.noFd}>
				<Else>
					<a
						href={`/trade/${countryParam}#/fixed-deposits/`}
						className="hover:bg-primary500 text-sm p-4 mx-2 rounded-md tracking-wider"
						title="Fixed deposits"
					>
						{t('Fixed Deposits', {
							ns: 'common',
						})}
					</a>
				</Else>
			</If>

			{countryProps.noFinanceExtras ? null : (
				<BbTooltips
					hoverFocus
					className="hidden sm:flex hover:bg-primary500 rounded-md tracking-wider"
					position={rightAligned ? 'bottomLeft' : 'bottomRight'}
					wrappedContent={
						<div className="c-links--light c-btn--iconTri tri-bottom text-sm p-4 mx-2 inline-block cursor-pointer text-white">
							{t('common:Finance')}
						</div>
					}
				>
					<div className="w-md">
						<a
							href={`/trade/${countryParam}#/ascent-ico-launch-purchase/`}
							title="Launchpad"
							className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine relative"
						>
							<div className="flex items-center tracking-normal">
								<img
									src={launchpadIcon.src}
									width="40"
									alt="hardWalletIcon"
								/>
								<div className="pl-4">
									<p className="text-primary200 font-bold">
										{t('common:Launchpad')}
									</p>
									<p className="text-gray-700 text-xs">
										{t('common:launchpadSubheading')}
									</p>
								</div>
							</div>
						</a>
						<a
							href={`/trade/${countryParam}#/hardware-cold-wallet`}
							className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine relative"
						>
							<div className="flex items-center tracking-normal">
								<img
									src={hardWalletIcon.src}
									width="40"
									alt="hardWalletIcon"
								/>
								<div className="pl-4">
									<p className="text-primary200 font-bold">
										{t('common:hardwareWallet')}
									</p>
									<p className="text-gray-700 text-xs">
										{t('common:hardwareWalletSubheading')}
									</p>
								</div>
							</div>
						</a>
						<a
							href={`/trade/${countryParam}#/pool-sales/`}
							title="Pool Sales"
							className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine relative"
						>
							<div className="flex items-center tracking-normal">
								<img
									src={poolSalesIcon.src}
									width="40"
									alt="hardWalletIcon"
								/>
								<div className="pl-4">
									<p className="text-primary200 font-bold">
										{t('common:poolSales')}
									</p>
									<p className="text-gray-700 text-xs">
										{t('common:poolSalesSubheading')}
									</p>
								</div>
							</div>
						</a>
					</div>
				</BbTooltips>
			)}
			<BbTooltips
				hoverFocus
				className="hidden sm:flex hover:bg-primary500 rounded-md tracking-wider"
				position={rightAligned ? 'bottomLeft' : 'bottomRight'}
				wrappedContent={
					<div className="c-links--light c-btn--iconTri tri-bottom text-sm p-4 mx-2 inline-block cursor-pointer text-white">
						{t('common:More')}
					</div>
				}
			>
				<div className="text-primary capitalize text-left w-sm">
					<a
						className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
						href="https://learn.bitbns.com/tokens-research-reports/"
					>
						{t('Research Reports', {
							ns: 'common',
						})}
					</a>
					<a
						className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
						href={`/trade/${countryParam}#/gift-vouchers/`}
					>
						{t('common:giftVoucher')}
					</a>
					<a
						className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
						href={`/trade/${countryParam}#/claim-rewards/`}
					>
						{t('common:claimRewards')}
					</a>
					<a
						className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
						href={`/trade/${countryParam}#/price-alerts/`}
					>
						{t('common:priceAlerts')}
					</a>
					<a
						className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
						href={`/trade/${countryParam}#/node-status/`}
					>
						{t('common:nodeStatus')}
					</a>
					{countryProps.noFinanceExtras ? (
						''
					) : (
						<a
							className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
							target="_blank"
							rel="noopener noreferrer"
							href="https://shop.bitbns.com?utm_source=bitbns&utm_medium=header_dd"
						>
							{t('common:visitBrandShop')}
						</a>
					)}
					<p className="bg-gray-200 text-gray-700 bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 py-2 text-sm">
						{t('common:apis')}
					</p>
					<a
						className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
						href={`/trade/${countryParam}#/api-trading/`}
					>
						{t('common:apiTrading')}
					</a>
					<a
						className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
						href={`/trade/${countryParam}#/sign-in-with-bitbns/`}
					>
						{t('common:bitbnsOAuthApi')}
					</a>
					<p className="bg-gray-200 text-gray-700 bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 py-2 text-sm">
						{t('common:Promotions')}
					</p>
					{countryProps.noPredictNWin ? (
						''
					) : (
						<a
							className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
							href={`/trade/${countryParam}#/predict-and-win/`}
						>
							{t('common:predictWin')}
						</a>
					)}
					<a
						className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
						href={`/trade/${countryParam}#/contests/`}
					>
						{t('common:Contests')}
					</a>
					{countryProps.noSprinkVouchers ? (
						''
					) : (
						<a
							className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
							href={`/trade/${countryParam}#/sprink-vouchers/`}
						>
							{t('common:sprinkVouchers')}
						</a>
					)}
				</div>
			</BbTooltips>
		</Fragment>
	);
};

const HeaderItemsMain = ({
	sideNotifsCount,
	isLoggedIn,
	__pushToMonitorEvents,
	__openSideNotifs,
	userAlertsCount,
	actionsRequiredUnreadCount,
	__logOut,
	minimal,
	submittedDetails: { email },
}) => {
	const [mountCount, updateMountCount] = useState(0);
	const { t } = useTranslation(['common'], {
		useSuspense: false,
	});

	useEffect(() => {
		requestIdleCb(() => {
			updateMountCount(1);
		});
	}, []);

	const { asPath } = useRouter();
	const country = asPath.split('/')[1];
	let countryProps = {};
	if (country) {
		countryProps = getCountryProps(country) || {};
	}

	const countryParam = country ? `?country=${country}` : '';
	const isTreasHunt = asPath.startsWith('/tr/treasure-hunt');
	const { bns, bnsPay, arbitrage } = getAppLinkFromCountry(country);

	return (
		<nav aria-labelledby="header-navigation">
			<div className="container flex justify-between py-5 items-center md:items-start z-30 relative">
				<div className="flex items-center">
					<div className="headerNav__logo flex items-center flex-shrink-0">
						{/* {selectUser === 1 && (
							<div
								css={css`
									transform: translateX(-7px);
									position: absolute;
									z-index: 1;
									height: 17px;
									align-self: flex-end;
								`}
							>
								<BbTooltips
									dark
									hoverFocus
									position="right"
									wrappedContent={
										<img
											src="https://bitbns.com/assets/img/diamond.png"
											alt="SELECT Member"
											title="You are a SELECT Member"
											height="17"
											width="17"
										/>
									}
								>
									<a
										href="https://medium.com/bitbns/introducing-bitbns-select-for-faster-inr-deposit-32cb01f0406"
										target="_blank"
										rel="noopener nofollow noreferrer"
										className="inline-block hover:underline p-2 u-para--readable font-light text-sm text-white whitespace-nowrap"
									>
										<Trans i18nKey="youAreSelect">
											You are a <em>SELECT</em> Member
										</Trans>
									</a>
								</BbTooltips>
							</div>
						)} */}
						<Link href="/">
							{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
							<a>
								<img
									src="https://bitbns.com/assets/img/logos/bitbns_new.svg"
									alt="bitbns Logo"
									width="90"
									className="headerNav__logo__img opacity-75"
								/>
							</a>
						</Link>
						<BbTooltips
							hoverFocus
							className="ml-5"
							position="bottomRight"
							wrappedContent={
								<div className="px-4 py-1">
									<img
										src={menu_icon.src}
										alt="menu_icon"
										width="16"
										height="16"
									/>
								</div>
							}
						>
							<div className="flex flex-col sm:flex-row text-gray-600">
								<div className="w-xs sm:w-sm bg-white p-8">
									<h5 className="tracking-widest text-sm">
										{t('common:RELATED')}
									</h5>
									<div>
										<div>
											<a
												href="https://bitdroplet.com/?utm_source=bitbns&utm_medium=web_header"
												className="flex items-start -mx-8 px-8 py-6 hover:bg-lavender mt-2"
											>
												<img
													src={bitdroplet_icon.src}
													width="25"
													height="25"
													alt="bitdroplet logo"
												/>
												<div className="-mt-1 ml-3 text-lg">
													<div className="text-primary">
														{t('common:Bitdroplet')}
													</div>
													<div className="text-sm">
														{t(
															'common:bdSubheading'
														)}
													</div>
												</div>
											</a>
										</div>
										<div>
											<a
												href="http://launchonascent.com/?utm_source=bitbns&utm_medium=web_header"
												className="flex items-start -mx-8 px-8 py-6 hover:bg-lavender mt-2"
											>
												<img
													src={ascent_icon.src}
													width="25"
													height="25"
													alt="ascent logo"
												/>
												<div className="-mt-1 ml-3 text-lg">
													<div className="text-primary">
														{t('common:Ascent')}
													</div>
													<div className="text-sm">
														{t(
															'common:ascentSubheading'
														)}
													</div>
												</div>
											</a>
										</div>
										<div>
											<a
												href="https://learn.bitbns.com/?utm_source=bitbns&utm_medium=web_header"
												className="flex items-start -mx-8 px-8 py-6 hover:bg-lavender mt-2"
											>
												<img
													src={
														bitbns_academy_icon.src
													}
													width="25"
													height="25"
													alt="bitbns academy logo"
												/>
												<div className="-mt-1 ml-3 text-lg">
													<div className="text-primary">
														{t('common:Ascent')}
													</div>
													<div className="text-sm">
														{t(
															'common:academySubheading'
														)}
													</div>
												</div>
											</a>
										</div>
									</div>
								</div>
								<div className="w-xs sm:w-sm whitespace-nowrap bg-gray-200 p-8">
									<div>
										<h5 className="tracking-widest text-sm">
											{t('BITBNS APPS', {
												ns: 'common',
											})}
										</h5>
										<div>
											<p className="my-5">
												<a
													href={`${bns.android}&referrer=utm_source=bitbnsTradeFtr&utm_medium=website`}
													target="_blank"
													rel="noreferrer noopener"
													className="hover:underline"
												>
													<img
														src={
															play_store_icon.src
														}
														width="25"
														height="25"
														alt="play store logo"
														className="inline-block mr-2"
													/>
													{t('common:playStore')}
												</a>
											</p>
											<p className="my-5">
												<a
													href={bns.ios}
													target="_blank"
													rel="noreferrer noopener"
													className="hover:underline"
												>
													<img
														src={app_store_icon.src}
														width="25"
														height="25"
														alt="app store logo"
														className="inline-block mr-2"
													/>
													{t('common:appStore')}
												</a>
											</p>
										</div>
									</div>
									<div className="my-12">
										<h5 className="tracking-widest text-sm">
											{t('BITBNS PAY APP', {
												ns: 'common',
											})}
										</h5>
										<div>
											<p className="my-5">
												<a
													href={bnsPay.android}
													target="_blank"
													rel="noreferrer noopener"
													className="hover:underline"
												>
													<img
														src={
															play_store_icon.src
														}
														width="25"
														height="25"
														alt="play store logo"
														className="inline-block mr-2"
													/>
													{t('common:playStore')}
												</a>
											</p>
											<p className="my-5">
												<a
													href={bnsPay.ios}
													target="_blank"
													rel="noreferrer noopener"
													className="hover:underline"
												>
													<img
														src={app_store_icon.src}
														width="25"
														height="25"
														alt="app store logo"
														className="inline-block mr-2"
													/>
													{t('common:appStore')}
												</a>
											</p>
										</div>
									</div>
									<div>
										<h5 className="tracking-widest text-sm">
											{t('common:ARBITRAGE')}
										</h5>
										<div>
											<p className="my-5">
												<a
													href={arbitrage.android}
													target="_blank"
													rel="noreferrer noopener"
													className="hover:underline"
												>
													<img
														src={
															play_store_icon.src
														}
														width="25"
														height="25"
														alt="play store logo"
														className="inline-block mr-2"
													/>
													{t('common:arbitragerApp')}
												</a>
											</p>
											<p className="my-5">
												<a
													href="https://chrome.google.com/webstore/detail/bitbns-extension/chohglfmmlfhnkloampcdmnnhnhegbfg?hl=en"
													target="_blank"
													rel="noreferrer noopener"
													className="hover:underline"
												>
													<img
														src={chrome_icon.src}
														width="25"
														height="25"
														alt="chrome logo"
														className="inline-block mr-2"
													/>
													{t(
														'common:chromeExtension'
													)}
												</a>
											</p>
										</div>
									</div>
								</div>
							</div>
						</BbTooltips>
					</div>
					<div className="items-center flex-wrap hidden md:inline-flex">
						<If condition={isTreasHunt}>
							<Then>
								<a
									href={`/trade/${countryParam}#/`}
									className="hover:bg-primary500 text-sm p-4 mx-2 rounded-md tracking-wider"
									title="About The Campaign"
								>
									{t('common:aboutTheCompetition')}
								</a>
								<a
									href={`/trade/${countryParam}#/profile/referrals/`}
									className="hover:bg-primary500 text-sm p-4 mx-2 rounded-md tracking-wider"
									title="FAQs"
								>
									{t('common:FAQs')}
								</a>
							</Then>
							<Else>
								<RenderNavLinks
									countryProps={countryProps}
									country={country}
								/>
							</Else>
						</If>
					</div>
				</div>

				<div className="flex items-center">
					<If condition={isTreasHunt}>
						<Else>
							{isLoggedIn === 1 && (
								<div className="items-center hidden sm:flex">
									<BbTooltips
										hoverFocus
										noTriangle
										noTransform
										className="c-hdr__items--right flex hover:bg-primary500 rounded-md"
										position="bottomLeft"
										wrappedContent={
											<div className="c-links--light c-btn--iconTri tri-bottom c-btn--vtiny p-4 mx-2 inline-block cursor-pointer text-white whitespace-nowrap">
												<span className="text-sm">
													{t('Orders')}
												</span>
											</div>
										}
									>
										<div className="text-primary capitalize text-left max-w-xs">
											<a
												className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
												href={`/trade/${countryParam}#/activity/`}
											>
												{t('common:openOrders')}
											</a>
											<a
												className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
												href={`/trade/${countryParam}#/activity/trade-history`}
											>
												{t('common:tradeHistory')}
											</a>
											<a
												className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
												href={`/trade/${countryParam}#/activity/activity/trade-refund-history`}
											>
												{t('common:tradeRefundHistory')}
											</a>
											<If
												condition={
													countryProps.noMargin
												}
											>
												<Else>
													<p className="bg-gray-200 text-gray-700 bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 py-2 text-sm">
														{t('common:Margin')}
													</p>
													<a
														className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal"
														href={`/trade/${countryParam}#/activity/margin-openorder`}
													>
														{t(
															'common:marginOpenOrder'
														)}
													</a>
													<a
														className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
														href={`/trade/${countryParam}#/activity/margin-ongoing-trades`}
													>
														{t(
															'common:ongoingTrades'
														)}
													</a>
													<a
														className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
														href={`/trade/${countryParam}#/activity/margin-history`}
													>
														{t('common:History')}
													</a>
												</Else>
											</If>
											<If condition={countryProps.noFd}>
												<Else>
													<p className="bg-gray-200 text-gray-700 bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 py-2 text-sm">
														{t('Fixed Income', {
															ns: 'common',
														})}
													</p>
													<a
														className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
														href={`/trade/${countryParam}#/activity/fixed-deposits-ongoing`}
													>
														{t('common:Ongoing')}
													</a>
													<a
														className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 block c-links u-text--singleLine tracking-normal relative"
														href={`/trade/${countryParam}#/activity/fixed-deposits-completed`}
													>
														{t('common:Completed')}
													</a>
												</Else>
											</If>
										</div>
									</BbTooltips>
									<a
										href={`/trade/${countryParam}#/wallets/`}
										className="c-hdr__items--right text-sm p-4 mx-2 rounded-md hover:bg-primary500"
										title="Wallets"
									>
										{t('common:Wallets')}
									</a>
								</div>
							)}
						</Else>
					</If>

					{!minimal && (
						<Button
							className="c-hdr__items--right mx-4 rounded-md focus:outline-none"
							variant="link"
							title="Notifications"
							onClick={() => {
								__openSideNotifs();
								__pushToMonitorEvents('Header Navigation', {
									action: 'Clicked on Notification Bell',
								});
							}}
						>
							<img
								src={bell_icon.src}
								alt="🔔"
								height="21"
								width="18"
								css={css`
									max-width: none;
								`}
							/>
							{(sideNotifsCount > 0 ||
								userAlertsCount > 0 ||
								actionsRequiredUnreadCount > 0) &&
								mountCount > 0 && (
									<div className="bb-notifBubble">
										{sideNotifsCount +
											userAlertsCount +
											actionsRequiredUnreadCount}
									</div>
								)}
						</Button>
					)}
					{isLoggedIn === 1 ? (
						<BbTooltips
							hoverFocus
							className="c-hdr__items--right mx-4 rounded-md inline-block"
							position="bottomLeft"
							onClick={() => {
								__pushToMonitorEvents('Header Navigation', {
									action: 'Clicked on Profile Icon',
								});
							}}
							wrappedContent={
								<img
									src={user_icon.src}
									alt="user"
									height="25"
									width="25"
									style={{
										maxWidth: 'none',
									}}
									title="User Profile"
								/>
							}
						>
							<div className="text-primary capitalize">
								{!!email && (
									<div
										className="bg-gray-200 p-2 px-5"
										title={email}
									>
										<p
											className="truncate lowercase text-gray-700"
											style={{ width: '150px' }}
										>
											{email}
										</p>
									</div>
								)}
								<Button
									className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 text-left w-full block c-links u-text--singleLine normal-case focus:outline-none"
									href={`/trade/${countryParam}#/profile`}
									variant="link"
									onClick={() => {
										__pushToMonitorEvents(
											'Header Navigation',
											{
												action: 'Clicked on Profile Link',
											}
										);
									}}
								>
									{t('common:Profile')}
								</Button>
								<Button
									className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 text-left w-full block c-links u-text--singleLine normal-case focus:outline-none"
									href={`/trade/${countryParam}#/profile/fee-slabs`}
									variant="link"
									onClick={() => {
										__pushToMonitorEvents(
											'Header Navigation',
											{
												action: 'Clicked on Profile Link',
											}
										);
									}}
								>
									{t('common:Fees')}
								</Button>
								<Button
									className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 text-left w-full block c-links u-text--singleLine normal-case focus:outline-none"
									href={`/trade/${countryParam}#/profile/referrals`}
									variant="link"
									onClick={() => {
										__pushToMonitorEvents(
											'Header Navigation',
											{
												action: 'Clicked on Profile Link',
											}
										);
									}}
								>
									{t('common:Referrals')}
								</Button>
								<Button
									className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 text-left w-full block c-links u-text--singleLine normal-case focus:outline-none"
									href={`/trade/${countryParam}#/profile/bank-accounts`}
									variant="link"
									onClick={() => {
										__pushToMonitorEvents(
											'Header Navigation',
											{
												action: 'Clicked on Profile Link',
											}
										);
									}}
								>
									{t('common:bankAccounts')}
								</Button>
								<Button
									onClick={() => {
										__logOut();
										__pushToMonitorEvents(
											'Header Navigation',
											{
												action: 'Clicked on Logout',
											}
										);
									}}
									variant="link"
									className="bb-loginTooltip__item c-link--nounderline hover:bg-purple-200 text-left c-links normal-case w-full focus:outline-none"
								>
									{t('common:logOut')}
								</Button>
							</div>
						</BbTooltips>
					) : (
						<div className="c-hdr__items--right">
							<Button
								size="tiny"
								href={`/trade/${countryParam}#/login/`}
								className="text-white normal-case  whitespace-nowrap mx-2 p-4"
								variant="minimal"
								onClick={() => {
									__pushToMonitorEvents('Header Navigation', {
										action: 'Clicked on Signin',
									});
								}}
							>
								{t('Log In')}
							</Button>

							<Button
								size="tiny"
								href={`/trade/${countryParam}#/signup/`}
								className="mx-2 p-4 normal-case whitespace-nowrap inline-block"
								variant="primary"
								onClick={() => {
									__pushToMonitorEvents('Header Navigation', {
										action: 'Clicked on Signup',
									});
								}}
							>
								{t('Sign Up')}
							</Button>
						</div>
					)}
				</div>
			</div>
			<div className="container md:hidden flex flex-wrap justify-end mb-5">
				<If condition={isTreasHunt}>
					<Then>
						<a
							href={`/trade/${countryParam}#/`}
							className="hover:bg-primary500 text-sm p-4 mx-2 rounded-md tracking-wider"
							title="About The Campaign"
						>
							{t('common:aboutTheCompetition')}
						</a>
						<a
							href={`/trade/${countryParam}#/profile/referrals/`}
							className="hover:bg-primary500 text-sm p-4 mx-2 rounded-md tracking-wider"
							title="FAQs"
						>
							{t('common:FAQs')}
						</a>
					</Then>
					<Else>
						<RenderNavLinks
							rightAligned
							countryProps={countryProps}
							country={country}
						/>
					</Else>
				</If>
			</div>
		</nav>
	);
};

export default connect(
	'isLoggedIn, sideNotifsCount, userAlertsCount, actionsRequiredUnreadCount, submittedDetails',
	actionsCombiner(monitorActions, sidebarActions, loginActions)
)(HeaderItemsMain);
